import { useMemo } from 'react';
import { v5 as uuidv5 } from 'uuid';
import { useSelector } from 'react-redux';
import { useQueryParams } from 'use-query-params';
import {
  buildParamsFromQuery,
  canonicalizeParams,
  getQueryParamConfig,
} from '../helpers/BillSearchHelper';
import { SEARCH_NAMESPACE } from '../utils/constants';
import { State } from '../dux/@types';
import {
  getDefaultJurisdictionFilter,
  getLegislaturesOptions,
} from '../helpers/JurisdictionSelectionHelper';
import { getTeamMode } from '../dux';
import { JurisdictionAPI } from '../api';
import { skipToken } from '@reduxjs/toolkit/dist/query';

// Deterministic search identifier: identical jurisdictions/query/teamId always
// hash to the same searchId, so events from the same search correlate without
// threading an ID through props or state.
export const computeSearchId = (
  jurisdictions: string[],
  query?: string,
  teamId?: number | null,
): string =>
  uuidv5(canonicalizeParams({ jurisdictions, query, teamId }), SEARCH_NAMESPACE);

export default function useSearchId() {
  const organizationUser = useSelector(
    (state: State) => state.account.organizationUser,
  );
  const { data: supportedJurisdictions } =
    JurisdictionAPI.endpoints.getUserSupportedJurisdictions.useQuery(
      organizationUser?.id ?? skipToken,
    );
  const orgJurisdictions = supportedJurisdictions ?? [];

  const legislaturesOptions = getLegislaturesOptions(orgJurisdictions);
  const jurisdictionFilters = getDefaultJurisdictionFilter(
    legislaturesOptions,
    organizationUser,
  );
  const [query] = useQueryParams(getQueryParamConfig(jurisdictionFilters));

  const teamModeId = useSelector(getTeamMode);

  const params = useMemo(() => {
    return buildParamsFromQuery(query, teamModeId, query.pageSize ?? 25);
  }, [query]);

  const searchId = useMemo(
    () =>
      computeSearchId(
        params.criteria.jurisdictions,
        params.criteria.query,
        params.criteria.teamId,
      ),
    [params.criteria.jurisdictions, params.criteria.query, params.criteria.teamId],
  );

  return {
    searchId,
    page: params.pagination.page,
    pageSize: params.pagination.pageSize,
  };
}
